
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, HelpCircle, Library } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BagCard } from "@/components/BagCard";
import { supabase } from "@/integrations/supabase/client";
import { BagData } from "@/types/bag";
import { useLocation, useNavigate } from "react-router-dom";

interface StyleSearchProps {
  onSearch: (id: string) => void;
  isLoading?: boolean;
}

type SearchResult = BagData;

export const StyleSearch = ({ onSearch, isLoading }: StyleSearchProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showFullResults, setShowFullResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.trim().length === 0) {
        setSearchResults([]);
        return;
      }

      const { data, error } = await supabase
        .rpc('search_bags', {
          search_query: searchQuery
        });

      if (error) {
        console.error('Error searching bags:', error);
        return;
      }

      // Sort the results to prioritize exact matches in name and style number
      const sortedResults = sortSearchResults(data || [], searchQuery);
      setSearchResults(sortedResults);
    };

    const timeoutId = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const sortSearchResults = (results: SearchResult[], query: string): SearchResult[] => {
    const normalizedQuery = query.trim().toLowerCase();
    
    return [...results].sort((a, b) => {
      // Check for exact match in Name (case-insensitive)
      const aNameExactMatch = a.Name?.toLowerCase().includes(normalizedQuery) || false;
      const bNameExactMatch = b.Name?.toLowerCase().includes(normalizedQuery) || false;
      
      // Check for exact match in style_number
      const aStyleExactMatch = a.style_number?.toString() === normalizedQuery;
      const bStyleExactMatch = b.style_number?.toString() === normalizedQuery;
      
      // Priority 1: Exact matches in style number (highest priority)
      if (aStyleExactMatch && !bStyleExactMatch) return -1;
      if (!aStyleExactMatch && bStyleExactMatch) return 1;
      
      // Priority 2: Exact matches in name (second-highest priority)
      if (aNameExactMatch && !bNameExactMatch) return -1;
      if (!aNameExactMatch && bNameExactMatch) return 1;
      
      // Priority 3: Name contains the query at the start
      const aNameStartsWith = a.Name?.toLowerCase().startsWith(normalizedQuery) || false;
      const bNameStartsWith = b.Name?.toLowerCase().startsWith(normalizedQuery) || false;
      
      if (aNameStartsWith && !bNameStartsWith) return -1;
      if (!aNameStartsWith && bNameStartsWith) return 1;
      
      // Priority 4: Alphabetical order by name
      if (a.Name && b.Name) {
        return a.Name.localeCompare(b.Name);
      }
      
      // If no name, sort by style number
      return (a.style_number || 0) - (b.style_number || 0);
    });
  };

  const handleSelect = (value: string) => {
    const selected = searchResults.find(result => 
      result.id === value ||
      result.style_number.toString() === value ||
      result.Name === value
    );
    
    if (selected) {
      onSearch(selected.id);
      setOpen(false);
      setSearchQuery("");
      setShowFullResults(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      setOpen(false);
      navigate(`/search/${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const formatStyleNumber = (number: number) => {
    return number.toString().padStart(4, '0');
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      <div className="relative" ref={containerRef}>
        <div className="relative flex items-center">
          <Input
            ref={inputRef}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setOpen(true);
              setShowFullResults(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search by style number or name..."
            className="h-12 pl-4 pr-24 text-sm sm:text-lg placeholder:text-sm sm:placeholder:text-lg bg-white/80 backdrop-blur-sm border-2 border-gold/20 focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-300"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="absolute right-12 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent hidden sm:flex"
                >
                  <HelpCircle className="h-5 w-5 text-muted-foreground hover:text-gold transition-colors" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>The style number is a 4 digit number embossed on the inside of your Coach bag. If your bag has a 7-8 digit serial number that starts with a letter, the last 4 digits are the style number.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            type="submit"
            disabled={isLoading || !searchQuery.trim()}
            onClick={handleSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 bg-gold hover:bg-gold-dark transition-colors duration-300"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
        
        {open && searchQuery.trim().length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-popover rounded-md border shadow-md">
            <Command>
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {searchResults.slice(0, 20).map((result) => (
                    <CommandItem
                      key={result.id}
                      value={result.id}
                      onSelect={handleSelect}
                      className="flex flex-col items-start py-3 cursor-pointer"
                    >
                      {result.Name ? (
                        <>
                          <div className="font-medium text-foreground">{result.Name}</div>
                          <div className="text-sm text-muted-foreground">Style {formatStyleNumber(result.style_number)}</div>
                        </>
                      ) : (
                        <div className="font-medium text-foreground">Style {formatStyleNumber(result.style_number)}</div>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4 pt-4">
        <Button onClick={() => navigate("/browse")}>Browse Bags</Button>
        <Button 
          variant="outline" 
          onClick={() => navigate("/catalogs")}
          className="flex items-center gap-2"
        >
          <Library className="w-4 h-4" />
          View Catalogs
        </Button>
      </div>
    </div>
  );
};
